/**
 * Helpers for "Add to calendar" (Google Calendar link + downloadable ICS) for consultation records.
 * Accepts both camelCase and PascalCase API shapes.
 */

function pick<T = unknown>(obj: any, ...keys: string[]): T | undefined {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") {
      return v as T;
    }
  }
  return undefined;
}

/** Parses strings like "1 hrs 0 mins", "30 mins". */
export function parseConsultationDurationMinutes(selectedTimeSlot: string | undefined | null): number {
  if (!selectedTimeSlot) return 60;
  let total = 0;
  const regex = /(\d+)\s*(hrs?|mins?)/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(String(selectedTimeSlot))) !== null) {
    const value = parseInt(m[1], 10);
    const unit = m[2].toLowerCase();
    if (unit.startsWith("hr")) total += value * 60;
    else if (unit.startsWith("min")) total += value;
  }
  return total > 0 ? total : 60;
}

function parseTimeToMinutes(time: string | undefined | null): number | null {
  if (!time) return null;
  const raw = String(time).trim().toLowerCase();
  const embedded = raw.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?/i);
  const candidate = embedded ? embedded[0] : raw;
  const match = candidate.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?$/i);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = (match[3] || "").toLowerCase();
  if (minutes < 0 || minutes > 59) return null;
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === "am") hours = hours === 12 ? 0 : hours;
    else hours = hours === 12 ? 12 : hours + 12;
  } else if (hours > 23) return null;
  return hours * 60 + minutes;
}

/** Builds local start/end from stored date + time + duration (same approach as dashboard feedback checks). */
export function getConsultationStartEnd(data: any): { start: Date; end: Date } | null {
  const dateRaw = pick<string>(data, "selectedDate", "SelectedDate");
  const timeRaw = pick<string>(data, "selectedTime", "SelectedTime");
  const slotRaw = pick<string>(data, "selectedTimeSlot", "SelectedTimeSlot");
  if (!dateRaw || !timeRaw) return null;

  const base = new Date(dateRaw as string | Date);
  if (isNaN(base.getTime())) return null;

  const mins = parseTimeToMinutes(timeRaw);
  if (mins === null) return null;

  const start = new Date(base);
  start.setHours(Math.floor(mins / 60), mins % 60, 0, 0);

  const durationMin = parseConsultationDurationMinutes(slotRaw);
  const end = new Date(start.getTime() + durationMin * 60 * 1000);
  return { start, end };
}

function escapeIcsText(s: string): string {
  return String(s || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r/g, "")
    .replace(/\n/g, "\\n");
}

function formatUtcIcs(dt: Date): string {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  const h = String(dt.getUTCHours()).padStart(2, "0");
  const min = String(dt.getUTCMinutes()).padStart(2, "0");
  const s = String(dt.getUTCSeconds()).padStart(2, "0");
  return `${y}${m}${d}T${h}${min}${s}Z`;
}

export function buildConsultationCalendarTitle(data: any): string {
  const creator = pick<string>(data, "creatorUserName", "CreatorUserName") || "Host";
  const customer = pick<string>(data, "customerUserName", "CustomerUserName");
  const content = pick<string>(data, "contentTitle", "ContentTitle");
  if (customer) return `Consultation: ${customer} & ${creator}`;
  if (content) return `Consultation: ${content}`;
  return `Consultation with ${creator}`;
}

function buildTitle(data: any): string {
  return buildConsultationCalendarTitle(data);
}

function buildDescription(data: any): string {
  const parts: string[] = [];
  const reason = pick<string>(data, "reasonForConsultation", "ReasonForConsultation");
  const tz = pick<string>(data, "selectedTimeZone", "SelectedTimeZone");
  if (reason) parts.push(`Reason: ${reason}`);
  if (tz) parts.push(`Timezone: ${tz}`);
  const link = pick<string>(data, "preferedMeetPlatform", "PreferedMeetPlatform");
  if (link) parts.push(`Meeting link: ${link}`);
  return parts.join("\n");
}

function buildLocation(data: any): string {
  return pick<string>(data, "preferedMeetPlatform", "PreferedMeetPlatform") || "";
}

export function openGoogleCalendarForConsultation(data: any): void {
  const range = getConsultationStartEnd(data);
  if (!range) return;
  const start = formatUtcIcs(range.start);
  const end = formatUtcIcs(range.end);
  const title = encodeURIComponent(buildTitle(data));
  const details = encodeURIComponent(buildDescription(data));
  const loc = encodeURIComponent(buildLocation(data));
  const dates = `${start}/${end}`;
  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&location=${loc}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function downloadConsultationIcs(data: any): void {
  const range = getConsultationStartEnd(data);
  if (!range) return;

  const uid =
    pick<string>(data, "rowKey", "RowKey") ||
    `${range.start.getTime()}@flowchest`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Flowchest//Consultation//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(uid)}`,
    `DTSTAMP:${formatUtcIcs(new Date())}`,
    `DTSTART:${formatUtcIcs(range.start)}`,
    `DTEND:${formatUtcIcs(range.end)}`,
    `SUMMARY:${escapeIcsText(buildTitle(data))}`,
    `DESCRIPTION:${escapeIcsText(buildDescription(data))}`,
    `LOCATION:${escapeIcsText(buildLocation(data))}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  const blob = new Blob([lines.join("\r\n")], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "consultation.ics";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function canBuildConsultationCalendarEvent(data: any): boolean {
  return getConsultationStartEnd(data) !== null;
}

/** For Outlook / CalendarLinkService and other providers that need structured fields. */
export function getConsultationCalendarEventPayload(data: any): {
  title: string;
  details: string;
  location: string;
  start: Date;
  end: Date;
} | null {
  const range = getConsultationStartEnd(data);
  if (!range) return null;
  return {
    title: buildTitle(data),
    details: buildDescription(data),
    location: buildLocation(data),
    start: range.start,
    end: range.end,
  };
}
